<template>
    <div id="content-editable-component">
        <Mentionable
            :keys="['#']"
            :items="list"
            @apply="preventBlur = true"
            @open="getList()"
            @close="isSnippetOpened = false"
            @search="getList($event)"
            :limit="list.length"
            filtering-disabled
            omit-key
        >
            <textarea
                v-model="message"
                @keydown="handleKeydown"
                :id="divId"
                :ref="divId"
                :disabled="disabled"
                @input="debouncedInputHandler"
                @focus="focus"
                @blur="blur"
                v-validate="required ? 'required' : null"
                :placeholder="placeholder"
                class="form-control"
                :class="[
                    className,
                    {
                        'auto-resize': resizeHeight,
                        'border-danger': errors.has(name)
                    }
                ]"
                :style="{
                    height: defaultHeight + 'px'
                }"
            />
            <template #no-result>
                <div class="loading-snippet">
                    {{ loading ? "Loading..." : "No result" }}
                </div>
            </template>
            <template #item-#="{ item }"> #{{ item.label }} </template>
        </Mentionable>
    </div>
</template>

<script type="text/javascript">
import {
    isUndefined,
    debounce,
    map,
    replace,
    filter,
    startsWith,
    isEmpty,
    trim
} from "lodash";
import { Mentionable } from "vue-mention";
import Templates from "@/services/templates";
import { DateTime } from "luxon";

const USERBROWSER = {
    SAFARI: "Safari",
    FIREFOX: "Firefox",
    CHROME: "Chrome"
};

export default {
    components: {
        Mentionable
    },
    props: {
        resizeHeight: {
            type: Boolean,
            default: false
        },
        defaultHeight: {
            type: Number, // in px
            default: null
        },
        divId: {
            type: String,
            default: "editable-div"
        },
        value: {
            type: String,
            default: ""
        },
        placeholder: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        isDebounceInput: {
            type: Boolean,
            default: false
        },
        charLimit: {
            type: Number,
            default: 0
        },
        required: {
            type: Boolean,
            default: false
        },
        className: {
            type: String,
            default: ""
        },
        name: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            focusIn: false,
            list: [],
            oriList: [],
            message: null,
            textareaHeight: 0,
            initialCursorPositionAndChar: null,
            preventBlur: false,
            loading: false,
            currentElement: null,
            isSnippetOpened: false,
            isEnterKeyPressed: false
        };
    },
    computed: {
        isSoap() {
            // by default this components is used in patient profile
            return this.$route.name === "soap";
        },
        userBrowser() {
            let userAgent = navigator.userAgent;
            // Check for Chrome
            if (userAgent.match(/firefox|fxios/i)) {
                return USERBROWSER.FIREFOX;
            }
            // Check for Safari
            else if (
                userAgent.match(/safari/i) &&
                !userAgent.match(/chrome|chromium|crios/i)
            ) {
                return USERBROWSER.SAFARI;
            } else {
                return USERBROWSER.CHROME;
            }
        }
    },
    watch: {
        message(val) {
            this.resetSnippetList(val);
            if (this.focusIn) {
                this.triggerUpdateSocket();
            }
            if (this.resizeHeight) {
                this.setEditableHeight();
            }
            if (this.charLimit > 0 && val.length > this.charLimit) {
                this.message = val.substring(0, this.charLimit);
            }

            // Check for '#date' and '#time'
            const dateTrigger = "#date";
            const timeTrigger = "#time";
            const timezone = this.$root?.setting?.timezone ?? this._timeZone;

            if (val) {
                // get cursor position
                const cursorPosition = this.currentElement.selectionStart;
                let newCursorPosition = cursorPosition;

                // if '#date' is typed, replace '#date' with current date time
                if (val.includes(dateTrigger)) {
                    const formatString = this._getLuxonFormat() + " hh:mm:ss a";
                    const timestamp = DateTime.now()
                        .setZone(timezone)
                        .toFormat(formatString);

                    let initials =
                        localStorage.getItem("my_staff_initials") || "";
                    if (initials !== "") {
                        initials = ` (${initials})`;
                    }

                    const replacement = `${timestamp}${initials}`;
                    const dateIndex = val.indexOf(dateTrigger);
                    newCursorPosition = dateIndex + replacement.length;

                    this.message = val.replace(dateTrigger, replacement);

                    // Set cursor position after Vue updates the DOM
                    this.$nextTick(() => {
                        this.currentElement.setSelectionRange(
                            newCursorPosition,
                            newCursorPosition
                        );
                    });
                }

                // if '#time' is typed, replace '#time' with current time
                if (val.includes(timeTrigger)) {
                    const time = DateTime.now()
                        .setZone(timezone)
                        .toFormat("hh:mm a");

                    const timeIndex = val.indexOf(timeTrigger);
                    newCursorPosition = timeIndex + time.length;

                    this.message = val.replace(timeTrigger, time);

                    // Set cursor position after Vue updates the DOM
                    this.$nextTick(() => {
                        this.currentElement.setSelectionRange(
                            newCursorPosition,
                            newCursorPosition
                        );
                    });
                }
            }

            this.$emit("input", this.message);
        },
        value(val) {
            if (this.message != val) {
                this.message = val;
            }
        }
    },
    created() {
        if (this.isDebounceInput) {
            this.debouncedInputHandler = debounce(this.update, 500);
        } else {
            this.debouncedInputHandler = this.update;
        }
    },
    mounted() {
        this.currentElement = this.$refs[this.divId];
        this.message = this.value;

        if (this.required) {
            this.$validator.attach(this.name, "required");
            this.$validator.reset(this.name);
        }

        if (this.resizeHeight && !this.isSoap) {
            this.setEditableHeight(30);
        }
    },
    methods: {
        triggerUpdateSocket() {
            let self = this;
            // currently only used by soap
            const value = this.message;

            if (!isUndefined(value)) {
                // Fire socket update to make sure other user get the latest editted input
                self.$emit("socket-update", self.divId, value);
            }
        },
        handleKeydown(event) {
            if (event.key === "Enter" && this.isSnippetOpened) {
                this.isEnterKeyPressed = true;
            }
        },
        async getList(searchText = null) {
            this.preventBlur = false;
            this.isSnippetOpened = true;
            this.isEnterKeyPressed = false;

            // will use for resetting snippet list
            this.initialCursorPositionAndChar = {
                positionDifference:
                    this.message.length - this.currentElement.selectionStart
            };
            if (searchText && searchText.length == 1) {
                // will use for resetting snippet list
                this.initialCursorPositionAndChar.position = this.currentElement.selectionStart;
                this.initialCursorPositionAndChar.character = searchText;
            }

            this.loading = true;
            this.oriList = [];
            await Templates.getAllSnippet()
                .then(response => {
                    this.oriList = this.transformData(response.resource.data);
                    if (searchText) {
                        this.list = filter(this.oriList, item => {
                            return startsWith(item.label, searchText);
                        });
                    } else {
                        this.list = this.oriList;
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        // Function to transform the array
        transformData(array) {
            return map(array, item => {
                // Remove '#' from the hashtag value
                let cleanHashtag = replace(item.hashtag, "#", "");

                // Convert to new format
                return {
                    value: item.elements + " ",
                    label: cleanHashtag
                };
            });
        },
        focus() {
            this.$emit("focus");
            this.focusIn = true;

            if (this.resizeHeight) {
                this.setEditableHeight();
            }
        },
        blur(e) {
            if (this.preventBlur && !this.isEnterKeyPressed) {
                // This to handle issue with the library when we select snippet using mouseclick, it loses focus. Thus, we are using flag to prevent from blurring.

                this.$nextTick(() => {
                    // fixes for browser issues
                    switch (this.userBrowser) {
                        case USERBROWSER.FIREFOX:
                            setTimeout(() => {
                                this.currentElement.focus();
                            }, 100);
                            break;
                        case USERBROWSER.SAFARI:
                            this.currentElement.focus();
                            this.setCursorLocation();
                            break;
                        default:
                            this.currentElement.focus();
                    }
                });
            } else {
                this.$emit("blur");
                this.focusIn = false;
            }
            this.preventBlur = false;
            this.isEnterKeyPressed = false;
        },
        update(e) {
            this.$emit("update", this.divId);
        },
        setEditableHeight(offset = 0) {
            // Save scroll position
            const scrollTop = window.scrollY; // For the entire window

            this.currentElement.style.height = "auto"; // Reset the height
            this.$nextTick(() => {
                this.currentElement.style.height =
                    this.currentElement.scrollHeight + offset + "px"; // resize the height

                // Restore scroll position after the frame is rendered
                window.scrollTo(0, scrollTop); // For the entire window
            });
        },
        updateValueThroughSocket(value) {
            this.message = value;
        },
        handleWindowResize() {
            if (this.resizeHeight) {
                this.setEditableHeight();
            }
        },
        resetSnippetList(value) {
            // Custom implemention to hande the vue-mention doesnt show full snippet when there is only # after initial text search.
            let currentCursorPosition = this.currentElement.selectionStart;
            if (
                this.initialCursorPositionAndChar &&
                this.initialCursorPositionAndChar.position &&
                this.initialCursorPositionAndChar.position - 1 ==
                    currentCursorPosition
            ) {
                let selectedText = value.substring(
                    currentCursorPosition,
                    this.initialCursorPositionAndChar.position
                );

                let isSelectedEmpty = isEmpty(trim(selectedText));
                if (
                    isSelectedEmpty ||
                    this.initialCursorPositionAndChar.character != selectedText
                ) {
                    this.list = this.oriList;
                    this.initialCursorPosition = null;
                }
            }
        },
        setCursorLocation() {
            // Fix for Safari only
            setTimeout(() => {
                const nextCursorPosition =
                    this.message.length +
                    (this.currentElement.selectionStart -
                        this.initialCursorPositionAndChar.positionDifference);
                this.currentElement.setSelectionRange(
                    nextCursorPosition,
                    nextCursorPosition
                );
            }, 100);
        },
        async validate() {
            return await this.$validator.validateAll();
        }
    }
};
</script>

<style>
.mention-item {
    padding: 4px 10px;
    border-radius: 4px;
}

.mention-selected {
    background: #dee0e2;
}

.loading-snippet {
    padding: 4px 10px;
    border-radius: 4px;
}
</style>

<style scoped>
.auto-resize {
    width: 100%;
    box-sizing: border-box; /* Include padding and border in element's total width and height */
}

.auto-resize:focus {
    overflow: hidden;
}
</style>
